import { useState } from 'react';
import Editor from '@monaco-editor/react';
import { Select } from 'antd';
import type { BodyMode, RawFormat, RequestBody, RequestBodyField } from '@core/entities/ApiRequest';
import { KeyValueRows } from '@presentation/components/workbench/KeyValueRows';
import { MultipartFieldRows } from '@presentation/components/workbench/MultipartFieldRows';
import { FilePicker, type FilePickerValue } from '@presentation/components/workbench/FilePicker';
import { GraphQLExplorerPanel } from '@presentation/components/workbench/GraphQLExplorerPanel';
import { useThemeStore } from '@presentation/stores/themeStore';

const MODE_OPTIONS: { label: string; value: BodyMode }[] = [
  { label: 'None', value: 'none' },
  { label: 'Raw', value: 'raw' },
  { label: 'Binary', value: 'binary' },
  { label: 'Multipart form', value: 'multipart_form' },
  { label: 'x-www-form-urlencoded', value: 'urlencoded' },
  { label: 'GraphQL', value: 'graphql' },
];

const RAW_FORMAT_OPTIONS: { label: string; value: RawFormat }[] = [
  { label: 'JSON', value: 'json' },
  { label: 'XML', value: 'xml' },
  { label: 'Text', value: 'text' },
  { label: 'HTML', value: 'html' },
  { label: 'JavaScript', value: 'javascript' },
];

const RAW_FORMAT_LANGUAGE: Record<RawFormat, string> = {
  json: 'json',
  xml: 'xml',
  html: 'html',
  javascript: 'javascript',
  text: 'plaintext',
};

const emptyBody = (mode: BodyMode): RequestBody => ({
  mode,
  rawFormat: mode === 'raw' ? 'json' : null,
  rawContent: null,
  graphqlQuery: null,
  graphqlVariables: null,
  binaryFilePath: null,
  fields: [],
});

const emptyField = (): RequestBodyField => ({
  id: crypto.randomUUID(),
  key: '',
  value: '',
  type: 'text',
  filePath: null,
  isEnabled: true,
  position: 0,
});

/**
 * Keeps the raw typed text as local state, independent of the parent's
 * `variables` prop, and only commits upstream once it parses as valid JSON.
 * The previous version passed invalid JSON straight through to a no-op,
 * which left the controlled textarea showing the last *valid* state and
 * silently discarded whatever the user had just typed — indistinguishable
 * from the keystroke never having registered.
 */
const GraphQLVariablesEditor = ({
  variables,
  onChange,
}: {
  variables: Record<string, unknown> | null;
  onChange: (variables: Record<string, unknown> | null) => void;
}) => {
  const themeMode = useThemeStore((state) => state.mode);
  const [text, setText] = useState(() => (variables ? JSON.stringify(variables, null, 2) : ''));
  const [error, setError] = useState<string | null>(null);

  const handleChange = (value: string | undefined) => {
    const next = value ?? '';
    setText(next);

    if (next.trim() === '') {
      setError(null);
      onChange(null);
      return;
    }

    try {
      const parsed: unknown = JSON.parse(next);
      setError(null);
      onChange(parsed as Record<string, unknown>);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-500">Variables (JSON)</label>
      <div className="h-32 overflow-hidden rounded border border-black/10 dark:border-white/10">
        <Editor
          height="100%"
          language="json"
          theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
          value={text}
          onChange={handleChange}
          options={{ minimap: { enabled: false }, fontSize: 12, scrollBeyondLastLine: false }}
        />
      </div>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};

interface BodyEditorProps {
  body: RequestBody | null;
  onChange: (body: RequestBody | null) => void;
  /** Used only by GraphQL mode's schema-explorer "Fetch schema" button. */
  requestUrl?: string;
  requestHeaders?: Record<string, string>;
}

export const BodyEditor = ({
  body,
  onChange,
  requestUrl = '',
  requestHeaders = {},
}: BodyEditorProps) => {
  const themeMode = useThemeStore((state) => state.mode);
  const mode = body?.mode ?? 'none';
  // Only the upload id round-trips through `binaryFilePath` (see RequestBody);
  // this session's own upload gives us the richer filename/size/mime to
  // display. A saved request reopened later — where that id refers to an
  // upload from a previous session — falls back to a generic placeholder
  // below rather than a blank field, since we never fetched its metadata.
  const [binaryMeta, setBinaryMeta] = useState<FilePickerValue | null>(null);

  const handleModeChange = (nextMode: BodyMode) => {
    onChange(nextMode === 'none' ? null : emptyBody(nextMode));
  };

  return (
    <div className="flex flex-col gap-3">
      <Select
        size="small"
        value={mode}
        options={MODE_OPTIONS}
        onChange={handleModeChange}
        className="w-48"
      />

      {mode === 'raw' && body && (
        <div className="flex flex-col gap-2">
          <Select
            size="small"
            value={body.rawFormat ?? 'json'}
            options={RAW_FORMAT_OPTIONS}
            onChange={(value: RawFormat) => onChange({ ...body, rawFormat: value })}
            className="w-32"
          />
          <div className="h-64 overflow-hidden rounded border border-black/10 dark:border-white/10">
            <Editor
              height="100%"
              language={RAW_FORMAT_LANGUAGE[body.rawFormat ?? 'json']}
              theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
              value={body.rawContent ?? ''}
              onChange={(value) => onChange({ ...body, rawContent: value ?? '' })}
              options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false }}
            />
          </div>
        </div>
      )}

      {mode === 'binary' && body && (
        <div className="flex flex-col gap-2">
          <FilePicker
            label="binary body"
            value={
              body.binaryFilePath
                ? binaryMeta?.uploadId === body.binaryFilePath
                  ? binaryMeta
                  : {
                      uploadId: body.binaryFilePath,
                      filename: 'Previously uploaded file',
                      mimeType: null,
                      size: 0,
                    }
                : null
            }
            onChange={(value) => {
              setBinaryMeta(value);
              onChange({ ...body, binaryFilePath: value?.uploadId ?? null });
            }}
          />
        </div>
      )}

      {mode === 'multipart_form' && body && (
        <MultipartFieldRows
          rows={body.fields}
          onChange={(fields) => onChange({ ...body, fields })}
          createRow={emptyField}
        />
      )}

      {mode === 'urlencoded' && body && (
        <KeyValueRows
          rows={body.fields}
          onChange={(fields) => onChange({ ...body, fields })}
          createRow={emptyField}
        />
      )}

      {mode === 'graphql' && body && (
        <div className="flex flex-col gap-2">
          <div className="h-48 overflow-hidden rounded border border-black/10 dark:border-white/10">
            <Editor
              height="100%"
              language="graphql"
              theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
              value={body.graphqlQuery ?? ''}
              onChange={(value) => onChange({ ...body, graphqlQuery: value ?? '' })}
              options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false }}
            />
          </div>

          <GraphQLVariablesEditor
            variables={body.graphqlVariables}
            onChange={(graphqlVariables) => onChange({ ...body, graphqlVariables })}
          />

          <GraphQLExplorerPanel url={requestUrl} headers={requestHeaders} />
        </div>
      )}
    </div>
  );
};
